(function () {
  'use strict';

  const STORAGE_KEY = 'sfd_order_form_items_v1';

  const toPositiveQuantity = (value) => {
    const quantity = Number.parseInt(value, 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  };

  const readItems = () => {
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      const items = stored ? JSON.parse(stored) : [];
      return Array.isArray(items) ? items : [];
    } catch (error) {
      console.warn('SFD Order Form: stored items could not be read.', error);
      return [];
    }
  };

  const writeItems = (items) => {
    const nextItems = Array.isArray(items) ? items : [];

    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextItems));
      window.dispatchEvent(
        new CustomEvent('sfd:order-form-updated', {
          detail: { items: nextItems }
        })
      );
    } catch (error) {
      console.warn('SFD Order Form: items could not be saved.', error);
    }

    return nextItems;
  };

  const normalizeItem = (item) => ({
    productId: String(item.productId || ''),
    productTitle: String(item.productTitle || ''),
    productHandle: String(item.productHandle || ''),
    productImage: String(item.productImage || ''),
    variantId: String(item.variantId || ''),
    variantTitle: String(item.variantTitle || ''),
    variantImage: String(item.variantImage || ''),
    quantity: toPositiveQuantity(item.quantity),
    product: item.product || null
  });

  const addItem = (item) => {
    const nextItem = normalizeItem(item);

    if (!nextItem.productId || !nextItem.variantId) {
      return readItems();
    }

    const items = readItems();
    const existing = items.find(
      (savedItem) =>
        String(savedItem.productId) === nextItem.productId &&
        String(savedItem.variantId) === nextItem.variantId
    );

    if (existing) {
      existing.quantity =
        toPositiveQuantity(existing.quantity) + nextItem.quantity;
      existing.product = existing.product || nextItem.product;
      existing.productTitle = existing.productTitle || nextItem.productTitle;
      existing.productHandle = existing.productHandle || nextItem.productHandle;
      existing.productImage = existing.productImage || nextItem.productImage;
      existing.variantTitle = existing.variantTitle || nextItem.variantTitle;
      existing.variantImage = existing.variantImage || nextItem.variantImage;
    } else {
      items.push(nextItem);
    }

    return writeItems(items);
  };

  window.SFDOrderFormStorage = {
    key: STORAGE_KEY,
    getItems: readItems,
    saveItems: writeItems,
    addItem
  };

  window.dispatchEvent(new Event('sfd:order-form-actions-ready'));

  const parseSelectedVariant = (root) => {
    const selectedVariant = root.querySelector(
      'variant-selects [data-selected-variant]'
    );

    if (!selectedVariant) return null;

    try {
      return JSON.parse(selectedVariant.textContent);
    } catch (error) {
      return null;
    }
  };

  const setTemporaryButtonMessage = (button, message) => {
    const label = button.querySelector('[data-order-add-label]');
    if (!label) return;

    const originalMessage = label.dataset.originalMessage || label.textContent;
    label.textContent = message;

    window.setTimeout(() => {
      if (button.dataset.added !== 'true') {
        label.textContent = originalMessage;
      }
    }, 1800);
  };

  const markButtonAsAdded = (button) => {
    const label = button.querySelector('[data-order-add-label]');
    if (!label) return;

    label.dataset.originalMessage = label.dataset.originalMessage || label.textContent;
    button.dataset.added = 'true';
    label.textContent = 'Added';
  };

  const resetButtonToAdd = (button) => {
    const label = button.querySelector('[data-order-add-label]');
    if (!label) return;

    label.textContent = label.dataset.originalMessage || 'Add to order form';
    delete button.dataset.added;
  };

  const getCurrentVariantId = (root) => {
    const productInfo = root.closest('product-info') || document;
    const inputVariantId = productInfo.querySelector(
      'product-form input[name="id"]'
    )?.value;
    const urlVariantId = new URLSearchParams(window.location.search).get('variant');

    return String(
      inputVariantId ||
      urlVariantId ||
      root.dataset.variantId ||
      ''
    );
  };

  const syncAddedButtons = (changedSectionId = null, changedVariantId = null) => {
    const items = window.SFDOrderFormStorage.getItems();

    document.querySelectorAll('[data-order-buttons]').forEach((root) => {
      const productInfo = root.closest('product-info');
      const usesChangedVariant =
        changedSectionId !== null &&
        String(productInfo?.dataset.section) === String(changedSectionId);
      const variantId = usesChangedVariant
        ? String(changedVariantId || '')
        : getCurrentVariantId(root);

      if (usesChangedVariant && variantId) {
        root.dataset.variantId = variantId;
      }
      const isAdded = items.some(
        (item) =>
          String(item.productId) === String(root.dataset.productId) &&
          String(item.variantId) === variantId
      );

      const button = root.querySelector('[data-order-add]');
      if (!button) return;

      if (isAdded) {
        markButtonAsAdded(button);
      } else {
        resetButtonToAdd(button);
      }
    });
  };

  window.SFDOrderFormStorage.syncVariant = (sectionId, variantId) => {
    syncAddedButtons(sectionId, variantId);
  };

  let syncFrame = null;

  const scheduleAddedStateSync = () => {
    if (syncFrame !== null) return;

    syncFrame = window.requestAnimationFrame(() => {
      syncFrame = null;
      syncAddedButtons();
    });
  };

  const observedProductInfos = new WeakSet();

  const observeProductInfo = (productInfo) => {
    if (!productInfo || observedProductInfos.has(productInfo)) return;

    observedProductInfos.add(productInfo);

    const observer = new MutationObserver((mutations) => {
      const variantDomChanged = mutations.some((mutation) => {
        if (mutation.type !== 'childList') return false;

        return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return false;

          return (
            node.matches?.('variant-selects, product-form') ||
            node.querySelector?.('variant-selects, product-form input[name="id"]')
          );
        });
      });

      if (variantDomChanged) scheduleAddedStateSync();
    });

    observer.observe(productInfo, {
      childList: true,
      subtree: true
    });
  };

  const observeProductInfos = () => {
    document.querySelectorAll('product-info').forEach(observeProductInfo);
  };

  const syncAfterVariantClick = (variantControl) => {
    const productInfo = variantControl.closest('product-info');
    const root = productInfo?.querySelector('[data-order-buttons]');
    if (!root) return;

    const previousVariantId = getCurrentVariantId(root);
    const startedAt = Date.now();

    const waitForSelectedVariant = () => {
      const currentVariantId = getCurrentVariantId(root);

      if (currentVariantId && currentVariantId !== previousVariantId) {
        syncAddedButtons();
        return;
      }

      if (Date.now() - startedAt < 1200) {
        window.setTimeout(waitForSelectedVariant, 40);
      } else {
        syncAddedButtons();
      }
    };

    window.setTimeout(waitForSelectedVariant, 0);
  };

  const addCurrentProduct = (button) => {
    const root = button.closest('[data-order-buttons]');
    const productInfo = button.closest('product-info') || document;
    const variantInput = productInfo.querySelector(
      'product-form input[name="id"]'
    );
    const quantityInput = productInfo.querySelector(
      '.quantity__input[name="quantity"]'
    );
    const selectedVariant = parseSelectedVariant(productInfo);

    const variantId = String(
      variantInput?.value || root?.dataset.variantId || ''
    );
    const quantity = toPositiveQuantity(quantityInput?.value || 1);

    if (!root || !variantId) {
      setTemporaryButtonMessage(button, 'Select a variant');
      return;
    }

    const variantTitle = selectedVariant?.title || root.dataset.variantTitle || '';
    const variantImage = root.dataset.variantImage || root.dataset.productImage || '';

    window.SFDOrderFormStorage.addItem({
      productId: root.dataset.productId,
      productTitle: root.dataset.productTitle,
      productHandle: root.dataset.productHandle,
      productImage: root.dataset.productImage,
      variantId,
      variantTitle,
      variantImage,
      quantity,
      product: {
        id: root.dataset.productId,
        title: root.dataset.productTitle,
        handle: root.dataset.productHandle,
        image: root.dataset.productImage || null,
        variants: [
          {
            id: variantId,
            title: variantTitle,
            available: true,
            image: variantImage || null
          }
        ]
      }
    });

    markButtonAsAdded(button);
  };

  document.addEventListener('click', (event) => {
    const addButton = event.target.closest('[data-order-add]');
    if (addButton) {
      event.preventDefault();
      addCurrentProduct(addButton);
    }
  });

  document.addEventListener('sfd:variant-changed', () => {
    scheduleAddedStateSync();
  });

  document.addEventListener(
    'click',
    (event) => {
      const variantControl = event.target.closest?.(
        'variant-selects input, variant-selects select, variant-selects label'
      );

      if (variantControl) syncAfterVariantClick(variantControl);
    },
    true
  );

  document.addEventListener('product-info:loaded', (event) => {
    observeProductInfo(event.target);
    scheduleAddedStateSync();
  });

  document.addEventListener(
    'change',
    (event) => {
      const changedVariantOption = event.target.closest?.('variant-selects');
      const changedVariantId = event.target.matches?.(
        'product-form input[name="id"]'
      );

      if (!changedVariantOption && !changedVariantId) return;

      scheduleAddedStateSync();
    },
    true
  );

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      syncAddedButtons(data.sectionId, data.variant?.id);
    });
  }

  window.addEventListener('sfd:order-form-updated', scheduleAddedStateSync);

  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) scheduleAddedStateSync();
  });

  window.addEventListener('pageshow', scheduleAddedStateSync);

  observeProductInfos();
  scheduleAddedStateSync();

  window.addEventListener('DOMContentLoaded', scheduleAddedStateSync, { once: true });
  window.addEventListener('load', scheduleAddedStateSync, { once: true });
  window.setTimeout(scheduleAddedStateSync, 0);
  window.setTimeout(scheduleAddedStateSync, 250);
})();
